/* ═══════════════════════════════════════════════════════════════
   Spec Masters Studio — the master switcher (Masters Ladder R3 §2.1;
   Cohesion R1 Phase 3; SPEC_WORKFLOW §1–2, plan H6; Feel-test R2 item 5).

   Two tiers, the same on every master. Row 1 is the MASTER — one wide pill
   "Office Master · v3", never hidden. Row 2 hangs beneath it off a thin
   rail: "Variants", one pill per variant (with the count of offers still
   waiting on it), then "+ New variant". Exactly ONE pill in the whole
   switcher is filled (and carries a check, so colour is not the only
   signal). The master pill is the way back from a variant. Selecting a
   variant recomposes the sheet and adds a context line ("Viewing variant:
   Richardson · 3 offers waiting").

   Every function of the old strip is kept: switch, add (name only),
   duplicate the active variant, delete it (specs.delete), per-variant
   waiting counts. In View, "+ New variant" is the shared View-mode guard:
   it explains itself and nudges Edit — it no longer opens the draft.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { createPortal } from 'react-dom';
import { Check } from 'lucide-react';
import type { ClientLayer } from '../../shared/types';
import { Icon } from '../components/ui/Icon';
import { InfoHint } from './InfoHint';
import { ViewModeGuard } from './useViewModeGuard';

const VARIANTS_EXPLAINER =
  "A master is the firm's text. A variant is a client's version of it. A project copy receives master updates for review; nothing flows back to the master.";

export interface VariantBarProps {
  layers: ClientLayer[];
  activeLayerId: string | null;
  /** The master's name for the top pill (e.g. "Office Master"). */
  masterLabel?: string;
  /** The master's published version. */
  masterVersion: number;
  /** May add / duplicate a variant right now (Edit mode + specs.edit_*). */
  canEdit: boolean;
  /** Deleting a variant discards its layer data irreversibly — gated
   *  on `specs.delete`, never `canEdit` (DESIGN §4 — destructive ⇒ permission). */
  canDelete: boolean;
  /** View (read-only) or Edit — only changes the context line's wording. */
  editing: boolean;
  onSwitch: (layerId: string | null) => void;
  onCreate: (name: string) => void;
  onDuplicate: (layerId: string, name: string) => void;
  onRequestDelete: (layer: ClientLayer) => void;
  /** How many offers are still waiting on this variant. */
  pendingUpdates?: (layerId: string | null) => number;
}

/** Arrow keys move focus within one radiogroup (roving tabIndex pattern, as
 *  components/ui/tabs.tsx). Enter / Space select through the button's click. */
function onRadioKeyDown(e: React.KeyboardEvent<HTMLButtonElement>) {
  const group = e.currentTarget.closest('[role="radiogroup"]');
  if (!group) return;
  const items = Array.from(group.querySelectorAll<HTMLButtonElement>('[role="radio"]'));
  const i = items.indexOf(e.currentTarget);
  let next = -1;
  if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (i + 1) % items.length;
  else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp')
    next = (i - 1 + items.length) % items.length;
  else if (e.key === 'Home') next = 0;
  else if (e.key === 'End') next = items.length - 1;
  if (next === -1) return;
  e.preventDefault();
  items[next]?.focus();
}

export function VariantBar({
  layers,
  activeLayerId,
  masterLabel = 'Office Master',
  masterVersion,
  canEdit,
  canDelete,
  editing,
  onSwitch,
  onCreate,
  onDuplicate,
  onRequestDelete,
  pendingUpdates,
}: VariantBarProps) {
  const [adding, setAdding] = React.useState<{ left: number; bottom: number } | null>(null);
  const [duplicating, setDuplicating] = React.useState<{ left: number; bottom: number } | null>(
    null
  );
  const active = layers.find((l) => l.id === activeLayerId) || null;
  const activeWaiting = active ? (pendingUpdates?.(active.id) ?? 0) : 0;
  const activeName = active?.name || 'this variant';
  const onMaster = activeLayerId === null;
  // Roving tabIndex: the selected pill, else the first, is the Tab stop.
  const variantStop = active ? active.id : (layers[0]?.id ?? null);

  return (
    <div className="sps-variants">
      <div className="sps-switch-master" role="radiogroup" aria-label="Master">
        <button
          type="button"
          role="radio"
          aria-checked={onMaster}
          tabIndex={0}
          className={`sps-pill${onMaster ? ' is-selected' : ''}`}
          onClick={() => onSwitch(null)}
          onKeyDown={onRadioKeyDown}
          title={`${masterLabel} — the firm's text, version ${masterVersion}`}
        >
          {onMaster && <Icon icon={Check} size="sm" aria-hidden="true" />}
          {masterLabel} · v{masterVersion}
        </button>
      </div>

      <div className="sps-variants-row">
        <span className="sps-variants-label" id="sps-variant-label">
          Variants
        </span>
        <div className="sps-vchips" role="radiogroup" aria-labelledby="sps-variant-label">
          {layers.map((layer) => {
            const waiting = pendingUpdates?.(layer.id) ?? 0;
            const name = layer.name || 'Variant';
            const on = activeLayerId === layer.id;
            return (
              <button
                key={layer.id}
                type="button"
                role="radio"
                aria-checked={on}
                tabIndex={layer.id === variantStop ? 0 : -1}
                className={`sps-pill${on ? ' is-selected' : ''}`}
                onClick={() => onSwitch(layer.id)}
                onKeyDown={onRadioKeyDown}
                title={
                  waiting > 0
                    ? `${name} — ${waiting} offer${waiting === 1 ? '' : 's'} from the master waiting`
                    : `${name} — this variant's layer on top of the master`
                }
              >
                {on && <Icon icon={Check} size="sm" aria-hidden="true" />}
                {name}
                {waiting > 0 && (
                  <span className="sps-count-pill">
                    {waiting}
                    <span className="sr-only"> offers waiting</span>
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <ViewModeGuard>
          <button
            type="button"
            className="sps-pill sps-pill-add"
            aria-haspopup="dialog"
            aria-expanded={!!adding}
            title={editing ? 'Add a variant' : undefined}
            onClick={(e) => {
              const r = e.currentTarget.getBoundingClientRect();
              setAdding((v) => (v ? null : { left: r.left, bottom: r.bottom }));
            }}
          >
            + New variant
          </button>
        </ViewModeGuard>
        <InfoHint label="Variants" text={VARIANTS_EXPLAINER} />

        {active && editing && (
          <span className="sps-variants-actions">
            <button
              type="button"
              className="dsg-btn sps-variant-dup"
              disabled={!canEdit}
              onClick={(e) => {
                const r = e.currentTarget.getBoundingClientRect();
                setDuplicating((v) => (v ? null : { left: r.left, bottom: r.bottom }));
              }}
              title={
                canEdit
                  ? `Copy ${activeName}'s adds, overrides and hidden blocks into a new variant`
                  : "You don't have permission to add a variant"
              }
            >
              Duplicate
            </button>
            <button
              type="button"
              className="dsg-btn sps-danger-btn sps-variant-remove"
              disabled={!canDelete}
              onClick={() => onRequestDelete(active)}
              title={
                canDelete
                  ? 'Delete this variant from the master.'
                  : "You don't have permission to delete a variant — ask an admin to grant Delete Specs"
              }
            >
              Delete variant
            </button>
          </span>
        )}
      </div>

      {active && (
        <div className="sps-variants-context" role="status">
          <span>
            {editing ? 'Editing variant: ' : 'Viewing variant: '}
            <strong>{active.name || 'Variant'}</strong>
            {activeWaiting > 0 &&
              ` · ${activeWaiting} offer${activeWaiting === 1 ? '' : 's'} waiting`}
            {editing && (
              <span className="sps-variant-hint">
                {' '}
                — everything you type lands on {activeName}&rsquo;s layer
              </span>
            )}
          </span>
        </div>
      )}

      {adding && (
        <NameVariantPopover
          anchor={adding}
          title="Add a variant"
          label="Variant name"
          submitLabel="Create variant"
          onSubmit={(name) => {
            setAdding(null);
            onCreate(name);
          }}
          onClose={() => setAdding(null)}
        />
      )}
      {duplicating && active && (
        <NameVariantPopover
          anchor={duplicating}
          title={`Duplicate ${active.name || 'this variant'}`}
          label="New variant name"
          submitLabel="Duplicate"
          defaultName={`${active.name || 'Variant'} copy`}
          onSubmit={(name) => {
            setDuplicating(null);
            onDuplicate(active.id, name);
          }}
          onClose={() => setDuplicating(null)}
        />
      )}
    </div>
  );
}

/* ─── "+ New variant" / "Duplicate" — a name, nothing else (portal, mousedown-outside) ─── */

function NameVariantPopover({
  anchor,
  title,
  label,
  submitLabel,
  defaultName,
  onSubmit,
  onClose,
}: {
  anchor: { left: number; bottom: number };
  title: string;
  label: string;
  submitLabel: string;
  defaultName?: string;
  onSubmit: (name: string) => void;
  onClose: () => void;
}) {
  const ref = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [name, setName] = React.useState(defaultName ?? '');

  React.useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [onClose]);

  React.useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const width = 320;
  const left = Math.min(anchor.left, window.innerWidth - width - 8);

  return createPortal(
    <div
      ref={ref}
      className="dsg-menu sps-variant-pop"
      role="dialog"
      data-app-layer=""
      aria-label={title}
      style={{ position: 'fixed', left, top: anchor.bottom + 4, width }}
    >
      <div className="sps-menu-label" aria-hidden="true">
        {title}
      </div>
      <div className="sps-variant-pop-row">
        <label className="sr-only" htmlFor="sps-variant-name">
          {label}
        </label>
        <input
          id="sps-variant-name"
          ref={inputRef}
          className="dsg-input"
          value={name}
          placeholder="Richardson"
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter' || !name.trim()) return;
            e.preventDefault();
            onSubmit(name);
          }}
        />
      </div>
      <div className="sps-variant-pop-actions">
        <button type="button" className="dsg-btn" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className="dsg-primary"
          disabled={!name.trim()}
          onClick={() => onSubmit(name)}
        >
          {submitLabel}
        </button>
      </div>
    </div>,
    document.body
  );
}
